/**
 * Routing Service
 *
 * Service for handling intelligent warehouse routing operations including:
 * - Calculating optimal routes for orders
 * - Retrieving routing metrics and costs
 * - Manual route overrides
 *
 * Maps to the backend `warehouse_order_router` endpoints.
 *
 * @module services/routingService
 */

import { api } from './api';

const BASE_URL = '/api/v1/warehouse-routing';

export const routingService = {
  /**
   * Calculate the optimal route for a set of items
   * @param {Object} routingRequest
   * @param {Array<Object>} routingRequest.items - List of items { product_id, quantity }
   * @param {Object} routingRequest.destination_address - Address details
   * @param {string} [routingRequest.strategy='OPTIMAL_STOCK'] - 'NEAREST_WAREHOUSE' | 'OPTIMAL_STOCK' | 'SPLIT_SHIPMENT'
   * @returns {Promise<Object>} Calculated route with warehouses and splits
   */
  async calculateRoute(routingRequest) {
    const response = await api.post(`${BASE_URL}/calculate`, routingRequest);
    return response.data;
  },

  /**
   * Get routing metrics and explanation for a specific order
   * @param {string} orderId
   * @returns {Promise<Object>} Routing decisions log and metrics
   */
  async getRoutingMetrics(orderId) {
    const response = await api.get(`${BASE_URL}/metrics/${orderId}`);
    return response.data;
  },

  /**
   * Force a re-route for an existing order
   * @param {string} orderId
   * @param {string} newStrategy - New routing strategy to apply
   * @returns {Promise<Object>} Updated order with new route
   */
  async rerouteOrder(orderId, newStrategy) {
    const response = await api.post(`${BASE_URL}/reroute/${orderId}`, { strategy: newStrategy });
    return response.data;
  },
  
  /**
   * Get available routing strategies
   * @returns {Promise<Array<string>>} List of supported strategies
   */
  async getStrategies() {
    // Assuming backend exposes this, or we fallback to static list
    const response = await api.get(`${BASE_URL}/strategies`);
    return response.data;
  }
};

export default routingService;
